/**
 * Screens 11 «Клиенты», 12 «Карточка клиента» and 12a, the client sheet
 * (salon-mockup.html clients / client / clientedit; D-59, D-60). The list
 * carries each client's counts from the API; channel pills show only what a
 * client really has, which before Phase 2 messaging is usually nothing. On the
 * phone each client is a slim card with her messenger logos on the right (D-83).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { AppBar, Sheet, Shell } from '../components/Shell';
import { ChannelLogo, Icon } from '../components/icons';
import { ApiError, api } from '../core/api';
import { errorText, formatAmount } from '../i18n';
import { serviceName } from '../lib/serviceName';
import { dayMonthShort, ymd } from '../lib/time';
import type { ClientCard, ClientListItem, Locale } from '../types';

const CHANNEL: Record<string, string> = { whatsapp: 'WhatsApp', telegram: 'Telegram', instagram: 'Instagram', sms: 'SMS' };
const dmy = (at: string) => ymd(at).split('-').reverse().join('.');
/** Messengers with a logo (the chat header's set); SMS has none (D-83). */
const logoChannels = (c: ClientListItem) => c.channels.filter((ch) => ch === 'whatsapp' || ch === 'telegram' || ch === 'instagram');
const Logos = ({ c }: { c: ClientListItem }) => (
  <span className="cl-ch">
    {logoChannels(c).map((ch) => (
      <ChannelLogo key={ch} channel={ch} />
    ))}
  </span>
);

export function ClientsScreen({ creating = false }: { creating?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const list = useQuery({ queryKey: ['clients', q.trim()], queryFn: () => api.clients(q.trim() || undefined, 200) });

  const sub = (c: ClientListItem) => {
    const visits = t('clients.visits', { count: c.visit_count });
    if (c.no_show_count) return `${visits} · ${t('clients.noShows', { count: c.no_show_count })}`;
    if (c.last_visit_at) return `${visits} · ${t('clients.last', { date: dayMonthShort(ymd(c.last_visit_at), locale) })}`;
    return visits;
  };

  return (
    <Shell active="clients">
      <AppBar
        title={t('clients.title')}
        right={
          <button type="button" className="iconbtn" aria-label={t('clients.add')} onClick={() => navigate('/clients/new')}>
            <Icon.plus />
          </button>
        }
      />
      <div style={{ flex: '0 0 auto', padding: '10px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <label className="inp" style={{ borderRadius: 999, minHeight: 36, padding: '7px 13px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon.srch />
          <input className="bare grow" type="search" aria-label={t('clients.search')} placeholder={t('clients.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      <div className="content">
        <div className="pane">
          <div className="mob-only">
            <ul className="cl-list">
              {list.data?.items.map((c) => (
                <li key={c.id} data-id={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                  <span className="av">{c.name.charAt(0)}</span>
                  <span className="grow">
                    <span className="nm ell">{c.name}</span>
                    <span className="sm ell" style={{ display: 'block' }}>
                      {sub(c)}
                    </span>
                  </span>
                  {logoChannels(c).length > 0 && <Logos c={c} />}
                </li>
              ))}
            </ul>
          </div>
          <div className="desk-only">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('clients.client')}</th>
                  <th>{t('clients.channel')}</th>
                  <th className="r">{t('clients.visitsCol')}</th>
                  <th className="r">{t('clients.noShowsCol')}</th>
                  <th className="r">{t('clients.total')}</th>
                  <th>{t('clients.lastCol')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.items.map((c) => (
                  <tr key={c.id} data-id={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                    <td>
                      <b>{c.name}</b>
                    </td>
                    <td>{logoChannels(c).length > 0 ? <Logos c={c} /> : '—'}</td>
                    <td className="r">{c.visit_count}</td>
                    <td className="r" style={c.no_show_count ? { color: 'var(--st-noshow-fg)', fontWeight: 700 } : undefined}>
                      {c.no_show_count}
                    </td>
                    <td className="r">{formatAmount(c.total_spent, locale)}</td>
                    <td>{c.last_visit_at ? dmy(c.last_visit_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {creating && <ClientSheet client={null} onClose={() => navigate('/clients')} onSaved={(c) => navigate(`/clients/${c.id}`, { replace: true })} />}
    </Shell>
  );
}

export function ClientScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { id, edit } = useParams();
  const card = useQuery({ queryKey: ['client', id], queryFn: () => api.client(id!) });
  const [contact, setContact] = useState<Contact | null>(null);
  const c = card.data;

  return (
    <Shell active="clients">
      <AppBar
        title={c?.name ?? ''}
        sub={t('clients.eyebrow')}
        back={() => navigate('/clients')}
        right={
          /* D-84: «+» books this client, the same «+» as on «Сегодня» and «Календарь». */
          <button type="button" className="iconbtn" aria-label={t('screen.newBooking')} onClick={() => navigate(`/appointments/new?client=${id}`)}>
            <Icon.plus />
          </button>
        }
      />
      {c && (
        <div className="content">
          <div className="pane pane-wide">
            <div className="row" style={{ marginBottom: 14 }}>
              <span className="av lg">{c.name.charAt(0)}</span>
              <span className="grow">
                <span className="nm" style={{ fontSize: 19 }}>
                  {c.name}
                </span>
                {/* D-85: the number and the messenger tag are buttons; each opens a sheet (12b, 12c). */}
                {c.phone && (
                  <button type="button" className="c-tel" style={{ display: 'flex' }} onClick={() => setContact({ kind: 'phone' })}>
                    <Icon.phone />
                    {c.phone}
                  </button>
                )}
                {cardTags(c).map((ch) => (
                  <span key={ch.channel + ch.external_id} style={{ display: 'block', marginTop: 5 }}>
                    {ch.channel === 'instagram' ? (
                      <>
                        <span className="c-tag off" aria-disabled="true">
                          <ChannelLogo channel={ch.channel} />
                          {CHANNEL[ch.channel]}
                        </span>
                        <span className="c-off">{t('clients.igOff')}</span>
                      </>
                    ) : (
                      <button type="button" className="c-tag" onClick={() => setContact({ kind: 'messenger', ch })}>
                        <ChannelLogo channel={ch.channel} />
                        {CHANNEL[ch.channel]}
                        <span className="chev">›</span>
                      </button>
                    )}
                  </span>
                ))}
                <button type="button" className="c-edit" onClick={() => navigate(`/clients/${c.id}/edit`)}>
                  {t('clients.edit')}
                </button>
              </span>
            </div>
            <div className="stats" style={{ marginBottom: 14 }}>
              <div className="stat">
                <b>{c.visit_count}</b>
                <span>{t('clients.visitsStat')}</span>
              </div>
              <div className="stat">
                <b>{formatAmount(c.total_spent, locale)}</b>
                <span>{t('clients.totalStat')}</span>
              </div>
              <div className="stat" style={c.no_show_count ? { borderColor: 'var(--st-noshow-bd)' } : undefined}>
                <b style={c.no_show_count ? { color: 'var(--st-noshow-fg)' } : undefined}>{c.no_show_count}</b>
                <span>{t('clients.noShowStat')}</span>
              </div>
            </div>
            {c.no_show_count > 0 && <div className="warnbox">{t('clients.noShowWarn', { count: c.no_show_count })}</div>}
            {c.notes && (
              <>
                <span className="cap">{t('clients.notes')}</span>
                <div className="card flat">{c.notes}</div>
              </>
            )}
            {/* D-62, D-84: on a phone the history follows the notes; booking is the header «+». */}
            <div className="mob-only" style={{ marginTop: 14 }}>
              <History card={c} />
            </div>
          </div>
          <div className="pane pane-2">
            <History card={c} />
          </div>
        </div>
      )}
      {c && contact && <ContactSheet card={c} contact={contact} onClose={() => setContact(null)} />}
      {c && edit === 'edit' && <ClientSheet client={c} onClose={() => navigate(`/clients/${c.id}`)} onSaved={() => navigate(`/clients/${c.id}`, { replace: true })} />}
    </Shell>
  );
}

type CardChannel = ClientCard['channels'][number];
type Contact = { kind: 'phone' } | { kind: 'messenger'; ch: CardChannel };

/** The card shows messenger tags only, WhatsApp → Telegram → Instagram (D-83); no SMS tag (D-85). */
const TAG_ORDER = ['whatsapp', 'telegram', 'instagram'];
const cardTags = (c: ClientCard) =>
  c.channels.filter((ch) => TAG_ORDER.includes(ch.channel)).sort((a, b) => TAG_ORDER.indexOf(a.channel) - TAG_ORDER.indexOf(b.channel));
/** `tel:` and `sms:` take digits and +; wa.me and t.me/+ take digits only. */
const dialable = (phone: string) => phone.replace(/[^\d+]/g, '');
const digits = (phone: string) => phone.replace(/\D/g, '');

/** Where «Позвонить через …» goes, or null for no call button (D-85). */
export function callLink(ch: CardChannel, phone: string | null): string | null {
  if (ch.channel === 'whatsapp') return phone ? `https://wa.me/${digits(phone)}` : null;
  if (ch.channel === 'telegram') {
    if (ch.display_name?.startsWith('@')) return `https://t.me/${ch.display_name.slice(1)}`;
    return phone ? `https://t.me/+${digits(phone)}` : null;
  }
  return null;
}

/** 12b (the number) and 12c (a messenger tag): a sheet of choices ending in «Отмена» (D-85). */
function ContactSheet({ card, contact, onClose }: { card: ClientCard; contact: Contact; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const phone = card.phone ?? '';
  let body;
  if (contact.kind === 'phone') {
    body = (
      <>
        <p className="lead">{phone}</p>
        <a className="c-act" href={`tel:${dialable(phone)}`}>
          <span className="ic">
            <Icon.phone />
          </span>
          <span>
            <b>{t('clients.call')}</b>
            <small>{t('clients.callPlain')}</small>
          </span>
        </a>
        <a className="c-act" href={`sms:${dialable(phone)}`}>
          <span className="ic">
            <Icon.sms />
          </span>
          <span>
            <b>{t('clients.sms')}</b>
          </span>
        </a>
      </>
    );
  } else {
    const app = CHANNEL[contact.ch.channel];
    const call = callLink(contact.ch, card.phone);
    body = (
      <>
        <p className="lead">{[app, card.phone ?? contact.ch.display_name].filter(Boolean).join(' · ')}</p>
        {call && (
          <a className="c-act" href={call}>
            <span className="ic">
              <ChannelLogo channel={contact.ch.channel} />
            </span>
            <span>
              <b>{t('clients.callVia', { app })}</b>
              <small>{t('clients.opens', { app })}</small>
            </span>
          </a>
        )}
        <button type="button" className="c-act" onClick={() => navigate(`/messages/${card.id}`)}>
          <span className="ic">
            <Icon.chat />
          </span>
          <span>
            <b>{t('clients.write')}</b>
            <small>{t('clients.writeHint')}</small>
          </span>
        </button>
      </>
    );
  }
  return (
    <Sheet onClose={onClose}>
      <h3>{card.name}</h3>
      {body}
      <button type="button" className="c-cancel" onClick={onClose}>
        {t('book.cancel')}
      </button>
    </Sheet>
  );
}

function History({ card }: { card: ClientCard }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const visits = card.visits.filter((v) => v.status !== 'cancelled');
  return (
    <>
      <span className="cap">{t('clients.history')}</span>
      <ul className="list">
        {visits.map((v) => (
          <li key={v.id} data-id={v.id}>
            <span className="tnum">{dmy(v.starts_at).slice(0, 5)}</span>
            <span className="grow">
              <span className="nm" style={{ fontSize: 13.5 }}>
                {serviceName(v.service_name_ru, v.service_name_ky, locale)}
              </span>
            </span>
            {v.status === 'no_show' ? <span className="pill warn">{t('clients.noShowPill')}</span> : <span className="tnum">{formatAmount(v.price, locale)}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

function ClientSheet({ client, onClose, onSaved }: { client: ClientCard | null; onClose: () => void; onSaved: (c: ClientCard) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(client?.name ?? '');
  const [phone, setPhone] = useState(client?.phone ?? '');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError(t('clients.nameNeeded'));
    const body = { name: name.trim(), phone: phone.trim() || null, notes: notes.trim() || null };
    setBusy(true);
    try {
      const saved = client ? await api.updateClient(client.id, body) : await api.createClient(body);
      qc.setQueryData(['client', saved.id], saved);
      // The name shows on appointments and the calendar too.
      for (const key of ['clients', 'today', 'day', 'week', 'appointment']) void qc.invalidateQueries({ queryKey: [key] });
      onSaved(saved);
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={onClose} className="narrow">
      <form onSubmit={submit} noValidate>
        <h3>{client ? client.name : t('clients.new')}</h3>
        {error && <div className="warnbox form-error" role="alert">{error}</div>}
        <div className="fld">
          <label>
            {t('clients.name')}
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </label>
        </div>
        <div className="fld">
          <label>
            {t('clients.phone')}
            <input className="inp tnum" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} placeholder="+996" />
          </label>
        </div>
        <div className="fld">
          <label>
            {t('clients.notes')}
            <textarea className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} style={{ minHeight: 72, resize: 'vertical', font: 'inherit' }} />
          </label>
        </div>
        <div className="btnrow">
          <button type="button" className="btn" onClick={onClose}>
            {t('services.cancel')}
          </button>
          <button type="submit" className="btn pri" disabled={busy}>
            {t('services.save')}
          </button>
        </div>
      </form>
    </Sheet>
  );
}
